import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';

import { IDashboardService } from '../../services/';
import { Store } from '../Store';
import * as DASHBOARDS from '../../stateManagement/dashboards/dashboards.constants';
import {
    loadDashboards,
    loadDashboardsSuccess,
    deleteDashboards,
    deleteDashboardsSuccess,
    updateDashboards,
    addDashboards,
    addDashboardsSuccess,
    deleteDashboard,
    deleteDashboardSuccess,
    deleteDashboardError
} from './dashboards.actions';
import { Dashboard, Category } from '../../models';
import { DashboardThumbnailWidget } from '../../widgets/dashboardThumbnail/dashboardThumbnail.widget';
import { IValueViewModelActionsService } from '../valueViewModels/valueViewModels.actionCreator';
import { IWidget } from '../../widgets';

export interface IDashboardActionsService {
    getOne(guid: string): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard>;
    getAll(): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    getAll(force: boolean): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    addOne(targetDashboard: Dashboard, filter?: boolean): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    addManyForApplication(targetDashboards: Dashboard[], appName: string, filter?: boolean): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    updateOne(targetDashboard: Dashboard, filter?: boolean): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    updateMany(targetDashboards: Dashboard[], filter?: boolean): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;
    deleteOne(guid: string): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard[]>;

    // Direct calls to dashboardService (no redux)
    getDashboardThumbnails(dashboards: Dashboard[]): DashboardThumbnailWidget[];
    getOrphanCategories(dashboards: Dashboard[], categories: Category[]): string[];
}

export class DashboardActionsService implements IDashboardActionsService {
    static serviceId = 'dashboardActionsService';
    static $inject = ['$q', 'dashboardService', 'valueViewModelActionsService'];

    constructor(
        private _$q: angular.IQService,
        private _dashboardService: IDashboardService,
        private _vvmas: IValueViewModelActionsService) {
    }

    getOne(guid: string): (dispatch: any, getState: () => Store) => angular.IPromise<Dashboard> {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (!dashboards.isLoading && !dashboards.isLoaded) {
                dispatch(loadDashboards());
                return this._dashboardService.getAll(true, true)
                    .then((data: Dashboard[]) => {
                        dispatch(loadDashboardsSuccess(data));
                        const dashboard = _.find(data, d => d.guid === guid);
                        return dashboard;
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_LOAD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                const found = _.find(_.values<Dashboard>(dashboards.dictionary), d => d.guid === guid);
                return this._$q.when(found);
            }
        };
    }

    getAll();
    getAll(force: boolean);
    getAll(force?: boolean) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (force || (!dashboards.isLoading && !dashboards.isLoaded)) {
                dispatch(loadDashboards());
                return this._dashboardService.getAll(force, true)
                    .then((data: Dashboard[]) => {
                        const dashboards = data;
                        dispatch(loadDashboardsSuccess(dashboards));
                        return dashboards;
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_LOAD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    addOne(targetDashboard: Dashboard, filter?: boolean) {
        return this.addMany([targetDashboard], filter);
    }

    addMany(targetDashboards: Dashboard[], filter?: boolean) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (targetDashboards && targetDashboards.length > 0) {
                dispatch(addDashboards(targetDashboards));
                // Convert Dashboard into DashboardThumbnailWidget for dashboardService
                const thumbnailsToUpdate: DashboardThumbnailWidget[] = this._dashboardService.getDashboardThumbnails(targetDashboards);
                const allDashboardThumbnails: DashboardThumbnailWidget[] = this._dashboardService.getDashboardThumbnails(_.values<Dashboard>(dashboards.dictionary));
                const maxRow: number = this.getMaxRow(allDashboardThumbnails);
                return this._dashboardService.addDashboards(thumbnailsToUpdate, maxRow, null, filter)
                    .then(data => {
                        const dashboards = data;
                        dispatch(addDashboardsSuccess(dashboards));
                        return dashboards;
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_ADD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    addManyForApplication(targetDashboards: Dashboard[], appGuid: string, filter?: boolean) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (targetDashboards && targetDashboards.length > 0) {
                dispatch(addDashboards(targetDashboards));
                // Convert Dashboard into DashboardThumbnailWidget for dashboardService
                const thumbnailsToUpdate: DashboardThumbnailWidget[] = this._dashboardService.getDashboardThumbnails(targetDashboards);
                const allDashboardThumbnails: DashboardThumbnailWidget[] = this._dashboardService.getDashboardThumbnails(_.values<Dashboard>(dashboards.dictionary));
                const maxRow: number = this.getMaxRow(allDashboardThumbnails);
                return this._dashboardService.addDashboards(thumbnailsToUpdate, maxRow, appGuid, filter)
                    .then(dashboards => {
                        dispatch(addDashboardsSuccess(dashboards));
                        return dashboards;
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_ADD_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    updateOne(targetDashboard: Dashboard, filter?: boolean) {
        return this.updateMany([targetDashboard], filter);
    }

    updateMany(targetDashboards: Dashboard[], filter?: boolean) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (targetDashboards && targetDashboards.length > 0) {
                dispatch(updateDashboards(targetDashboards));
                // Convert Dashboard into DashboardThumbnailWidget for dashboardService
                const thumbnailsToUpdate: DashboardThumbnailWidget[] = this._dashboardService.getDashboardThumbnails(targetDashboards);
                return this._dashboardService.updateDashboards(thumbnailsToUpdate, filter)
                    .then(dashboards => {
                        dispatch(this._vvmas.getAll(true));
                        return dispatch(this.getAll(true));
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_UPDATE_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    deleteOne(guid: string) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (guid) {
                dispatch(deleteDashboard(guid));
                return this._dashboardService
                    .deleteDashboard(guid)
                    .then(data => {
                        const dashboards = data;
                        dispatch(deleteDashboardSuccess(dashboards));
                        dispatch(this._vvmas.getAll(true));
                        return dashboards;
                    })
                    .catch((reason) => {
                        dispatch(deleteDashboardError(reason));
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    deleteMany(guids: string[]) {
        return (dispatch: any, getState: () => Store) => {
            const { dashboards } = getState();
            if (guids && guids.length > 0) {
                dispatch(deleteDashboards(guids));
                return this._dashboardService
                    .deleteDashboards(guids)
                    .then(data => {
                        const dashboards = data;
                        dispatch(deleteDashboardsSuccess(dashboards));
                        dispatch(this._vvmas.getAll(true));
                        return dashboards;
                    })
                    .catch((reason) => {
                        dispatch({ type: DASHBOARDS.DASHBOARDS_DELETE_ERROR, payload: reason });
                        throw reason;
                    });
            } else {
                return this._$q.when(_.values<Dashboard>(dashboards.dictionary));
            }
        };
    }

    getDashboardThumbnail(dashboard: Dashboard): DashboardThumbnailWidget {
        const thumbnails = this.getDashboardThumbnails([dashboard]);
        if (thumbnails && thumbnails.length > 0) {
            return thumbnails[0];
        } else {
            return null;
        }
    }

    getDashboardThumbnails(dashboards: Dashboard[]): DashboardThumbnailWidget[] {
        return this._dashboardService.getDashboardThumbnails(dashboards);
    }

    getMaxRow(widgetList: IWidget[]): number {
        let maxRow: number = 0;
        if (widgetList && widgetList.length > 0) {
            maxRow = d3.max(widgetList, (w: IWidget) => w.row + w.sizeY);
        }
        return maxRow + 1;
    }

    getOrphanCategories(dashboards: Dashboard[], categories: Category[]): string[] {
        let linkedCategoriesGuid: string[] = [];

        // Get all categories linked to dashboards
        dashboards.forEach(dashboard => {
            linkedCategoriesGuid = linkedCategoriesGuid.concat(dashboard.categories);
        });

        // Remove duplicates
        linkedCategoriesGuid = _.uniq(linkedCategoriesGuid);

        // Get orphan categories
        const categoryGuidToDelete: string[] = categories
            .filter(c => linkedCategoriesGuid.indexOf(c.guid) < 0)
            .map(c => c.guid);

        return categoryGuidToDelete;
    }
}